/**
 * DOCUMENTATION:
 * Component: SystemDesignQueues
 * Purpose: Teaching asynchronous communication using Message Queues (RabbitMQ, Kafka).
 * * * Note for Junior Developers:
 * - Producer: The service that sends a message to the queue.
 * - Consumer: The service that picks up and processes the message.
 * - Decoupling: The Producer doesn't need to know if the Consumer is alive. It just drops the message and moves on.
 * - Smoothing Spikes: If 1,000 users upload photos at once, the queue holds the jobs so the
 * processing server doesn't crash. It just works through the pile at its own pace.
 */

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
} from "@mui/material";

/* Icons */
import QueueIcon from '@mui/icons-material/Queue';
import MultipleStopIcon from '@mui/icons-material/MultipleStop';
import SpeedIcon from '@mui/icons-material/Speed';
import SyncAltIcon from '@mui/icons-material/SyncAlt';

const bgImage = "https://images.unsplash.com/photo-1451187580459-43490279c0fa";

/* Styled Components */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(10, 5, 25, 0.9), rgba(10, 5, 25, 0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(20, 15, 45, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    color: "white",
}));

const ModelBox = styled(Box)(({ theme, accent }) => ({
    padding: theme.spacing(3),
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.02)",
    border: `1px solid \${accent || "rgba(255, 255, 255, 0.1)"}`,
    height: "100%",
}));

export default function SystemDesignQueues() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h5" : "h3"} sx={{ fontWeight: 800, color: "#00c8ff" }}>
                    MESSAGE QUEUES & EVENTS
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* 1. Synchronous vs Asynchronous */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><SyncAltIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Why Go Asynchronous?</Typography>
                        </Box>
                        <Typography mb={4} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            In a <strong>Synchronous</strong> system, Service A calls Service B and waits for a response. If Service B is slow, Service A hangs.
                            In an <strong>Asynchronous</strong> system, Service A sends a message to a <strong>Message Queue</strong> and immediately
                            returns a "Success" to the user.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={2} p={2} bgcolor="rgba(255,107,107,0.05)" borderRadius="8px" border="1px solid rgba(255,107,107,0.2)">
                                    <Box><Typography fontWeight={700} color="#ff6b6b">Synchronous Risk</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>Cascading failures: If the billing service is down, the whole checkout process crashes.</Typography></Box>
                                </Stack>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Stack direction="row" spacing={2} p={2} bgcolor="rgba(74,222,128,0.05)" borderRadius="8px" border="1px solid rgba(74,222,128,0.2)">
                                    <Box><Typography fontWeight={700} color="#4ade80">Asynchronous Benefit</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.7 }}>Resilience: The queue stores messages safely until the billing service comes back online.</Typography></Box>
                                </Stack>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 2. Messaging Models */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#ffb300" }}><MultipleStopIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Common Messaging Models</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9 }}>
                            Depending on your needs, you might use a direct queue or a broadcast system.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 1 }}>
                            <Grid item xs={12} md={6}>
                                <ModelBox accent="#00c8ff">
                                    <Typography variant="h6" fontWeight={700} gutterBottom>Point-to-Point (Queue)</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        One message is processed by exactly one consumer.
                                        <strong> Example:</strong> Sending an email after a purchase.
                                    </Typography>
                                </ModelBox>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ModelBox accent="#a78bfa">
                                    <Typography variant="h6" fontWeight={700} gutterBottom>Publish/Subscribe (Pub-Sub)</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        One message is broadcast to all interested subscribers.
                                        <strong> Example:</strong> A "New Product" event that triggers an email, updates the search index, and alerts the analytics engine.
                                    </Typography>
                                </ModelBox>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 3. Event-Driven Architecture */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "#00c8ff" }}><QueueIcon /></Avatar>
                            <Typography variant="h5" fontWeight={800}>Event-Driven Systems</Typography>
                        </Box>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.8 }}>
                            In an <strong>Event-Driven Architecture (EDA)</strong>, the system reacts to "events" (state changes).
                            Instead of Service A telling Service B to "Do X", Service A simply announces "X Happened."
                            This is the gold standard for high-scale, decoupled systems.
                        </Typography>



                        <Stack spacing={2} sx={{ mt: 3 }}>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography color="#00c8ff" fontWeight={700}>Backpressure</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>Queues help manage load. If your consumers are too slow, the queue grows, giving you time to spin up more consumer servers.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                <Typography color="#00c8ff" fontWeight={700}>Idempotency</Typography>
                                <Typography variant="body2" sx={{ opacity: 0.7 }}>Critical Concept: Your consumers must be able to handle the same message twice without causing duplicate work (e.g., charging a user twice).</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/system-design-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#00c8ff", color: "#0a0519", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
